'use client';

import { LocaleDetails } from 'shared/src/types/languages.type';
import { Sitemap } from 'shared/src/types/sitemap.type';
import { useState, useCallback, useMemo, memo, useEffect } from 'react';
import { sitemapUiContent } from '@/lib/ui-content/sitemap-ui-content';
import { checkIsAdminAction } from '@/server/sitemap/check-admin-action';

type SitemapComponentProps = {
  subcategories: Sitemap[];
  locale: LocaleDetails;
  depth?: number;
  expandedState?: Record<string, boolean>;
  onToggleExpand?: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
};

export const SitemapSpecific = ({
  category,
  subcategories,
  locale,
}: {
  category: Sitemap;
  subcategories: Sitemap[];
  locale: LocaleDetails;
}) => {
  const ui = sitemapUiContent[locale.languageCode];

  // Initialize with smart defaults - expand product pages by default for better UX
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() => {
    const initialState: Record<string, boolean> = {};
    const initializeExpanded = (items: Sitemap[]) => {
      items.forEach((item) => {
        // Auto-expand product pages for immediate content visibility
        if (item.isProductPage) {
          initialState[item.id] = true;
        }
        // Recursively process subcategories
        if (item.subcategories && item.subcategories.length > 0) {
          initializeExpanded(item.subcategories);
        }
      });
    };
    initializeExpanded(subcategories);
    return initialState;
  });

  // Helper function to collect all IDs recursively
  const collectAllIds = useCallback((items: Sitemap[]): Record<string, boolean> => {
    const allIds: Record<string, boolean> = {};
    const collectIds = (categoryItems: Sitemap[]) => {
      categoryItems.forEach((item) => {
        if (item.subcategories && item.subcategories.length > 0) {
          allIds[item.id] = true;
          collectIds(item.subcategories);
        }
      });
    };
    collectIds(items);
    return allIds;
  }, []);

  // Helper function to collect IDs by depth level
  const collectIdsByDepth = useCallback((items: Sitemap[], targetDepth: number, currentDepth = 0): Record<string, boolean> => {
    const allIds: Record<string, boolean> = {};

    const collectIds = (categoryItems: Sitemap[], depth: number) => {
      categoryItems.forEach((item) => {
        if (item.subcategories && item.subcategories.length > 0) {
          if (depth === targetDepth) {
            allIds[item.id] = true;
          }
          if (depth < targetDepth) {
            collectIds(item.subcategories, depth + 1);
          }
        }
      });
    };

    collectIds(items, currentDepth);
    return allIds;
  }, []);

  const expandAll = useCallback(() => {
    const allIds = collectAllIds(subcategories);
    setExpandedCategories(allIds);
  }, [subcategories, collectAllIds]);

  const collapseAll = useCallback(() => {
    setExpandedCategories({});
  }, []);

  const stepByStepExpand = useCallback(() => {
    const currentExpandedIds = new Set(Object.keys(expandedCategories).filter((id) => expandedCategories[id]));

    // Find the next depth level to expand
    let depthToExpand = 0;
    let hasFoundNextLevel = false;

    // Check each depth level to find the next one to expand
    while (depthToExpand < 10 && !hasFoundNextLevel) {
      // Max depth check to prevent infinite loop
      const idsAtDepth = collectIdsByDepth(subcategories, depthToExpand);
      const idsAtDepthArray = Object.keys(idsAtDepth);

      if (idsAtDepthArray.length > 0) {
        // Check if all IDs at this depth are already expanded
        const allExpanded = idsAtDepthArray.every((id) => currentExpandedIds.has(id));

        if (!allExpanded) {
          // Expand this level
          setExpandedCategories((prev) => ({
            ...prev,
            ...idsAtDepth,
          }));
          hasFoundNextLevel = true;
        }
      }

      depthToExpand++;
    }

    // If no more levels to expand, we're done
    if (!hasFoundNextLevel) {
      console.log('All levels already expanded');
    }
  }, [subcategories, expandedCategories, collectIdsByDepth]);

  return (
    <div className="space-y-4" key={category.id}>
      {/* Enhanced Category Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <a
              className="group inline-flex items-center text-xl font-bold text-gray-900 transition-colors hover:text-cornflowerblue dark:text-white"
              href={`${category.href?.[locale.path.replace(locale.regionCode, 'ALL')]?.replace('ALL', locale.regionCode) || '#'}`}
            >
              <span>{category.title}</span>
              {category.totalSubcategoriesCount && (
                <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                  ({category.totalSubcategoriesCount} {ui.categories})
                </span>
              )}
              <svg
                className="ml-2 h-5 w-5 text-cornflowerblue transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>

            {category.description && (
              <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{category.description}</p>
            )}
          </div>

          <div className="ml-4 flex items-center space-x-2 max-sm:flex-col max-sm:space-x-0 max-sm:space-y-2">
            <button
              onClick={expandAll}
              className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-cornflowerblue transition-colors hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
            >
              <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              {ui.expandAll}
            </button>
            <button
              onClick={collapseAll}
              className="inline-flex items-center rounded-full bg-gray-50 px-3 py-1 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              <svg className="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              {ui.collapseAll}
            </button>
          </div>
        </div>
      </div>

      {/* Subcategories */}
      {subcategories && subcategories.length > 0 && (
        <div className="ml-6 sm:pl-6 max-sm:ml-3 max-sm:pr-1">
          <SitemapComponent
            subcategories={subcategories}
            locale={locale}
            depth={0}
            expandedState={expandedCategories}
            onToggleExpand={(updater) => setExpandedCategories(updater)}
          />
        </div>
      )}
    </div>
  );
};

// Memoized category item component to prevent unnecessary re-renders
const CategoryItem = memo(
  ({
    category,
    depth,
    locale,
    getDepthStyling,
    getTextStyling,
    isExpanded,
    onToggleExpand,
    expandedState,
  }: {
    category: Sitemap & { hasSubcategories: boolean };
    depth: number;
    locale: LocaleDetails;
    getDepthStyling: (depth: number) => string;
    getTextStyling: (depth: number) => string;
    isExpanded: boolean;
    onToggleExpand?: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
    expandedState?: Record<string, boolean>;
  }) => {
    const { hasSubcategories } = category;
    const ui = sitemapUiContent[locale.languageCode];
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminCheckLoading, setAdminCheckLoading] = useState(true);

    // Check admin status on component mount
    useEffect(() => {
      const checkAdminStatus = async () => {
        try {
          const adminStatus = await checkIsAdminAction();
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('Failed to check admin status:', error);
          setIsAdmin(false);
        } finally {
          setAdminCheckLoading(false);
        }
      };

      checkAdminStatus();
    }, []);

    const handleToggle = useCallback(() => {
      if (onToggleExpand) {
        onToggleExpand((prev) => ({
          ...prev,
          [category.id]: !prev[category.id],
        }));
      }
    }, [onToggleExpand, category.id]);

    return (
      <div className={getDepthStyling(depth)}>
        {/* Category Header */}
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center">
            <a
              className={`${getTextStyling(depth)} group flex items-center truncate text-gray-900 transition-colors hover:text-cornflowerblue dark:text-white`}
              href={`${category.href?.[locale.path.replace(locale.regionCode, 'ALL')]?.replace('ALL', locale.regionCode) || '#'}`}
            >
              <span className="truncate">{category.title}</span>

              {/* Status Indicators */}
              <div className="ml-2 flex flex-shrink-0 items-center space-x-1">
                {category.isProductPage && !hasSubcategories && (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/20 dark:text-green-400">
                    <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                    {ui.products}
                  </span>
                )}

                {hasSubcategories && (
                  <>
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                      <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                      {category.subcategories!.length}
                    </span>
                  </>
                )}

                {!category.isProductPage && !hasSubcategories && (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/20 dark:text-red-400">
                    <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    {ui.empty}
                  </span>
                )}

                <svg
                  className="h-4 w-4 text-cornflowerblue transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </a>
          </div>
        </div>

        {/* Description */}
        {category.description && (
          <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-400">{category.description}</p>
        )}

        {/* Individual Expand/Collapse Button for non-product categories */}
        {!category.isProductPage && hasSubcategories && (
          <div className="mt-3 flex justify-center">
            <button
              onClick={handleToggle}
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                isExpanded
                  ? 'bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400 dark:hover:bg-orange-900/30'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/30'
              }`}
            >
              <svg
                className={`mr-1 h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              {isExpanded ? ui.collapse : ui.expand}
            </button>
          </div>
        )}

        {/* Nested Subcategories */}
        {hasSubcategories && isExpanded && (
          <div className="ml-1 mt-4 sm:border-l-2 border-gray-200 pl-4 max-sm:pl-0 dark:border-gray-600 max-sm:ml-0">
            <SitemapComponent
              subcategories={category.subcategories!}
              locale={locale}
              depth={depth + 1}
              expandedState={expandedState}
              onToggleExpand={onToggleExpand}
            />
          </div>
        )}

        {/* Enhanced Debug Info - Show for admins only */}
        {(process.env.NODE_ENV === 'development' || isAdmin) && !adminCheckLoading && (
          <details className="mt-4 rounded bg-gray-100 p-3 text-xs dark:bg-gray-700">
            <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-300">
              {isAdmin ? '🔒 Admin Debug Info' : ui.debugInfo} - {category.title}
            </summary>
            <div className="mt-2 space-y-3">
              {/* Admin Status Indicator */}
              {isAdmin && (
                <div className="rounded bg-green-100 p-2 dark:bg-green-900/20">
                  <span className="font-semibold text-green-800 dark:text-green-400">✅ Admin Access Verified</span>
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-1">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200">Basic Info:</h4>
                <div className="flex">
                  <strong className="w-24 text-gray-600 dark:text-gray-400">ID:</strong>
                  <span className="text-gray-800 dark:text-gray-200">{category.id}</span>
                </div>
                <div className="flex">
                  <strong className="w-24 text-gray-600 dark:text-gray-400">Title:</strong>
                  <span className="text-gray-800 dark:text-gray-200">{category.title}</span>
                </div>
                <div className="flex">
                  <strong className="w-24 text-gray-600 dark:text-gray-400">Product Page:</strong>
                  <span className="text-gray-800 dark:text-gray-200">{category.isProductPage ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex">
                  <strong className="w-24 text-gray-600 dark:text-gray-400">Subcategories:</strong>
                  <span className="text-gray-800 dark:text-gray-200">{category.subcategories?.length || 0}</span>
                </div>
              </div>

              {/* HREF Object */}
              {category.href && (
                <div className="space-y-1">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200">HREF Paths:</h4>
                  <div className="ml-2 space-y-1">
                    {Object.entries(category.href).map(([locale, path]) => (
                      <div key={locale} className="flex">
                        <strong className="w-16 text-gray-600 dark:text-gray-400">{locale}:</strong>
                        <span className="break-all text-blue-600 dark:text-blue-400">{path}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {category.description && (
                <div className="space-y-1">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200">Description:</h4>
                  <p className="ml-2 text-gray-600 dark:text-gray-400">{category.description}</p>
                </div>
              )}

              {/* Best Features */}
              {category.bestFeatures && Array.isArray(category.bestFeatures) && category.bestFeatures.length > 0 && (
                <div className="space-y-1">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200">Best Features:</h4>
                  <ul className="ml-2 list-disc space-y-1 text-gray-600 dark:text-gray-400">
                    {category.bestFeatures.map((feature: string, index: number) => (
                      <li key={index} className="ml-4">
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Complete Object Dump */}
              <details className="space-y-1">
                <summary className="cursor-pointer font-semibold text-gray-800 dark:text-gray-200">Complete Object Data</summary>
                <div className="ml-2 mt-2 space-y-1">
                  {Object.keys(category).map((key) => (
                    <div key={key} className="flex">
                      <strong className="w-24 text-gray-600 dark:text-gray-400">{key}:</strong>
                      <span className="break-all text-gray-800 dark:text-gray-200">
                        {typeof (category as any)[key] === 'object' || typeof (category as any)[key] === 'boolean'
                          ? JSON.stringify((category as any)[key], null, 2)
                          : (category as any)[key]}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          </details>
        )}

        {/* Loading indicator for admin check */}
        {adminCheckLoading && process.env.NODE_ENV !== 'development' && <></>}
      </div>
    );
  },
);

CategoryItem.displayName = 'CategoryItem';

export const SitemapComponent = memo(
  ({ subcategories, locale, depth = 0, expandedState, onToggleExpand }: SitemapComponentProps) => {
    // Use local state if not provided from parent (for nested components)
    const [localExpandedCategories, setLocalExpandedCategories] = useState<Record<string, boolean>>({});

    // Use parent state if available, otherwise use local state
    const expandedCategories = expandedState || localExpandedCategories;
    const setExpandedCategories = onToggleExpand || setLocalExpandedCategories;

    // Memoize styling functions to prevent re-computation on every render
    const getDepthStyling = useCallback((currentDepth: number) => {
      const baseClasses = 'rounded-lg border transition-all duration-200 hover:shadow-md';

      switch (currentDepth) {
        case 0:
          return `${baseClasses} bg-gray-50 dark:bg-dark/50 border-gray-200 dark:border-gray-700 p-4`;
        case 1:
          return `${baseClasses} bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-600 p-3`;
        default:
          return `${baseClasses} bg-gray-25 dark:bg-gray-900/30 border-gray-100 dark:border-gray-600 p-2`;
      }
    }, []);

    const getTextStyling = useCallback((currentDepth: number) => {
      switch (currentDepth) {
        case 0:
          return 'text-lg font-semibold';
        case 1:
          return 'text-base font-medium';
        default:
          return 'text-sm font-normal';
      }
    }, []);

    // Memoize the categories to prevent unnecessary re-processing
    const processedCategories = useMemo(() => {
      return subcategories.map((category) => {
        const hasSubcategories = !!(category.subcategories && category.subcategories.length > 0);
        const isExpanded = expandedCategories[category.id] || !!category.isProductPage;

        return {
          ...category,
          hasSubcategories,
          isExpanded,
        };
      });
    }, [subcategories, expandedCategories]);

    return (
      <div className="space-y-3">
        {processedCategories.map((category) => (
          <CategoryItem
            key={category.id}
            category={category}
            depth={depth}
            locale={locale}
            getDepthStyling={getDepthStyling}
            getTextStyling={getTextStyling}
            isExpanded={category.isExpanded}
            onToggleExpand={onToggleExpand}
            expandedState={expandedState}
          />
        ))}
      </div>
    );
  },
);

SitemapComponent.displayName = 'SitemapComponent';
